import React, { useState, useEffect } from 'react'
import PropTypes from 'prop-types'
import FormStepsNav from '../../2_Molecules/FormStepsNav/FormStepsNav'
import { languageController } from '../../../controllers/language/languageController'
import StepWrapper from '../../2_Molecules/StepWrapper/StepWrapper'
import { displayOutsideComponents, hideOutsideComponents, pushToGTMDataLayer, scrollToTop, handleAnalyticsTrackingCodes } from '../../../utils/helpers'
import VehicleInformationStep from './VehicleInformationStep/VehicleInformationStep'
import ServiceDetailsStep from './ServiceDetailsStep/ServiceDetailsStep'
import FindDealerStep from './FindDealerStep/FindDealerStep'
import DateAndTimeStep from '../../4_Templates/DateAndTimeStep/DateAndTimeStep'
import ConfirmationStep from './ConfirmationStep/ConfirmationStep'
import ContactDetailsStep from './ContactDetailsStep/ContactDetailsStep'

function BookServiceAppointment({ formExtraParams }) {
  const { language, lastStepBottomContents, initStepTopContents, introductionText, formDisclaimer } = formExtraParams
  const maxStepNum = 6;
  const initStep = 3;

  const [currentStep, setCurrentStep] = useState(initStep)
  const [currentProgress, setCurrentProgress] = useState(initStep)
  const [data, setData] = useState({})

  // Display top contents on the init step.
  useEffect(() => {
    if (currentStep === initStep) {
      displayOutsideComponents(initStepTopContents)
    }
    // Add the form-related vars to DTM dataLayer.
    pushToGTMDataLayer({ form: 'Book a Service Appointment' });
    pushToGTMDataLayer({ formid: 'BaSA' });
  }, [])

  // Update 'formstep' dataLayer var when currentStep is changed
  useEffect(() => {
    pushToGTMDataLayer({ formstep: `Step${currentStep}` });
  }, [currentStep])

  const handleNextStep = (newProps) => {
    if (currentStep === maxStepNum) return;

    const nextStep = currentStep + 1;

    // Manage visibility of components outside of the react app.
    if (nextStep === initStep + 1) {
      // Hide TiE Hero and Intro section
      hideOutsideComponents(initStepTopContents)
    } else if (nextStep === maxStepNum) {
      // Display TiE related bottom contents on confirmation step.
      displayOutsideComponents(lastStepBottomContents)
    }

    setCurrentStep(nextStep)

    setData({ ...data, ...newProps })
    initAnalyticsTrackingCodes({ ...data, ...newProps }, currentStep)

    setCurrentProgress(prevState => nextStep > prevState ? nextStep : prevState)

    // Scroll to the top of the screen.
    scrollToTop();
  }

  // Handle adding analytics tracking attributes.
  const initAnalyticsTrackingCodes = async (data, completedStep) => {
    //handleAnalyticsTrackingCodes
    try {
      // Set vehicle-related tracking attributes
      if (completedStep === 1 && data.vehicleData) {
        const trackData = { modelCarlineName : data.vehicleData.model, modelYear : data.vehicleData.modelYear, kilometers : data.vehicleData.kilometers, vin :data.vehicleData.vin };
        handleAnalyticsTrackingCodes(trackData, 'Vehicle Select');
      }
      // Set dealer-related tracking attributes
      if (completedStep === 3 && data.dealerData) {
        const trackData = { dealerName : data.dealerData.dealer?.name };
        handleAnalyticsTrackingCodes(trackData, 'Dealer Select');
      }
      // Set user-related tracking attributes
      if (completedStep === 5 && data.contactDetails) {
        const trackData = { email : data.contactDetails.email, firstname: data.contactDetails.firstname, lastname : data.contactDetails.lastname, phone : data.contactDetails.phone, postalcode : data.contactDetails.postalcode, optin : data.contactDetails.optin === 'on' ? 'optin' : 'optout' };
        handleAnalyticsTrackingCodes(trackData, 'Contact Details Submit');
      }
    } catch (err) {
      console.log(err)
    }
  }

  return (
    <div
      className="mz-book-service-appointment"
      data-current-step={currentStep}
    >
      {/* <FormStepsNav
        isActive={(currentStep < maxStepNum)}
        currentStep={currentStep}
        currentProgress={currentProgress}
        setCurrentStep={nextStep => {
          // If selected step is init step, show init top contents.
          if (nextStep === initStep) {
            displayOutsideComponents(initStepTopContents)
          }
          // If selected step is greater than init step, hide init top contents.
          else if (nextStep > initStep) {
            hideOutsideComponents(initStepTopContents)
          }
          setCurrentStep(nextStep)
        }}
        navList={[
          {
            step: 1,
            label: languageController.getCopy('bookServiceAppointment', 'step1'),
          },
          {
            step: 2,
            label: languageController.getCopy('bookServiceAppointment', 'step2'),
          },
          {
            step: 3,
            label: languageController.getCopy('bookServiceAppointment', 'step3'),
          },
          {
            step: 4,
            label: languageController.getCopy('bookServiceAppointment', 'step4'),
          },
          {
            step: 5,
            label: languageController.getCopy('bookServiceAppointment', 'step5'),
          },
        ]}
      /> */}
      <div className="mz-book-service-appointment__steps">
        {/* Step 1: Vehicle info */}
        <StepWrapper
          className={`mz-book-service-appointment__vehicle-information`}
          stepNum={1}
          currentStep={currentStep}
          currentProgress={currentProgress}
        >
          <VehicleInformationStep
            handleNextStep={handleNextStep}
            topText={introductionText}
          />
        </StepWrapper>
        {/* Step 2: Service details */}
        {/* <StepWrapper
          className={`mz-book-service-appointment__service-details`}
          type="select-dealer"
          stepNum={2}
          currentStep={currentStep}
          currentProgress={currentProgress}
        >
          <ServiceDetailsStep
            handleNextStep={handleNextStep}
            language={language}
          />
        </StepWrapper> */}
        {/* Step 3: Select a dealer */}
        <StepWrapper
          className={`mz-book-service-appointment__select-dealer`}
          stepNum={3}
          currentStep={currentStep}
          currentProgress={currentProgress}
        >
          <FindDealerStep
            handleNextStep={handleNextStep}
            vehicleData={data.vehicleData}
            displayServiceHoursOnly={true}
          />
        </StepWrapper>
        {/* Step 4: Date and time */}
        <StepWrapper
          className={`mz-book-service-appointment__date-and-time`}
          stepNum={4}
          currentStep={currentStep}
          currentProgress={currentProgress}
        >
          <DateAndTimeStep
            handleNextStep={handleNextStep}
            title={languageController.getCopy('global', 'selectDateAndTime')}
            subTitle={languageController.getCopy('bookServiceAppointment', 'step4SubTitle')}
            isService={true}
          />
        </StepWrapper>
        {/* Step 5: Contact details */}
        <StepWrapper
          className={`mz-book-service-appointment__value-estimate`}
          stepNum={5}
          currentStep={currentStep}
          currentProgress={currentProgress}
        >
          <ContactDetailsStep
            handleNextStep={handleNextStep}
            dealerData={data.dealerData}
            dateAndTimeData={data.dateAndTime}
            vehicleData={data.vehicleData}
            serviceDetails={data.serviceDetails}
            formLanguage={language}
            formDisclaimer={formDisclaimer}
          />
        </StepWrapper>
        {/* Step 6: Confirmation */}
        <StepWrapper
          className={`mz-book-service-appointment__value-estimate`}
          stepNum={6}
          currentStep={currentStep}
          currentProgress={currentProgress}
        >
          <ConfirmationStep
            contactDetails={data.contactDetails}
            vehicleData={data.vehicleData}
            dealerData={data.dealerData}
            dateAndTimeData={data.dateAndTime}
          />
        </StepWrapper>
      </div>
    </div>
  )
}

BookServiceAppointment.propTypes = {
  formExtraParams: PropTypes.object.isRequired
}

BookServiceAppointment.defaultProps = {
  formExtraParams: {}
}

export default BookServiceAppointment